import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import Slider from "react-slick";
import { AppDispatch, RootState } from "../../../apis/store";
import { toGetBanners } from "../../../apis/actions/HeroSection.actions";
import { baseUrlApi } from "../../../proxy";

const AUTOPLAY_MS = 7000;
const FADE_MS = 950;

type Banner = {
  // ponytail: Odoo sends `false` for empty strings
  first_title?: string | false;
  second_title?: string | false;
  url?: string;
  link?: string;
  image?: string;
  image_url?: string;
  banner_image?: string;
  photo?: string;
  background?: string;
};

type HeroSlide = Banner & { alternateBgOnly?: boolean };

function normalizeBanners(raw: unknown): Banner[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as Banner[];
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data as Banner[];
    if (Array.isArray(o.result)) return o.result as Banner[];
    if (Array.isArray(o.banners)) return o.banners as Banner[];
  }
  return [];
}

function bannerImageUrl(b: Banner | null): string | undefined {
  if (!b) return undefined;
  const raw =
    b.image ??
    b.image_url ??
    b.banner_image ??
    b.photo ??
    b.background;
  if (typeof raw !== "string" || !raw.trim()) return undefined;
  return raw.trim();
}

/** ERP often returns paths relative to API origin; `html[dir=rtl]` must not break resolution. */
function resolveBannerSrc(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("//")) return `https:${s}`;
  const pub = process.env.PUBLIC_URL ?? "";
  if (s.startsWith("/")) return `${pub}${s}`;
  try {
    return new URL(s, baseUrlApi).href;
  } catch {
    return `${pub}/${s.replace(/^\/+/, "")}`;
  }
}

const fallbackBg = (index: number) => {
  const base = process.env.PUBLIC_URL || "";
  const imgs = [
    `${base}/assets/imgs/hero/hero-3/banner.png`,
    `${base}/assets/imgs/hero/hero-3/banner-2.png`,
  ];
  return imgs[index % imgs.length];
};

/** No i18n fallback copy — empty API still gets stock images + CTA only. */
function buildHeroSlides(banners: Banner[]): HeroSlide[] {
  if (banners.length > 0) {
    return banners.map((b) => ({ ...b }));
  }
  return [
    { url: "/offers" },
    { url: "/offers", alternateBgOnly: true },
  ];
}

function slideHeroImageSrc(item: HeroSlide, index: number): string {
  if (item.alternateBgOnly) {
    return fallbackBg(1);
  }
  const api = bannerImageUrl(item);
  if (api) return resolveBannerSrc(api);
  return fallbackBg(index % 2);
}

const captionContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.06,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.28, ease: [0.4, 0, 1, 1] as const },
  },
};

const captionItem = {
  hidden: {
    opacity: 0,
    y: 22,
    filter: "blur(6px)",
  },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const sliderRef = useRef<Slider>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [cookies] = useCookies(["apiKey"]);
  const [activeIndex, setActiveIndex] = useState(0);
  /** Bumps on every Slick settle so the rail restarts even if activeIndex stays the same (infinite/clones). */
  const [railEpoch, setRailEpoch] = useState(0);
  const [railProgress, setRailProgress] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const slidesLenRef = useRef(0);
  const prevSlidesLengthRef = useRef<number | undefined>(undefined);
  const railRafRef = useRef<number>();
  const railSlideStartRef = useRef(0);
  const railPauseSumRef = useRef(0);
  const railPauseBeginRef = useRef<number | null>(null);

  const { list, status } = useSelector(
    (state: RootState) => state.heroSection.Banners
  );

  const banners = useMemo(() => normalizeBanners(list), [list]);
  const slides = useMemo(() => buildHeroSlides(banners), [banners]);
  slidesLenRef.current = slides.length;
  const current = slides[activeIndex] ?? slides[0];

  const isRtl = i18n.language === "ar";
  const isLoading = status === "idle";
  const multi = slides.length > 1;

  useEffect(() => {
    dispatch(toGetBanners(i18n.language));
  }, [dispatch, i18n.language]);

  const refreshSliderLayout = useCallback(() => {
    const slick = sliderRef.current;
    if (!slick) return;
    const inner = (
      slick as unknown as { innerSlider?: { onWindowResized?: () => void } }
    ).innerSlider;
    inner?.onWindowResized?.();
  }, []);

  const stopRailAnim = useCallback(() => {
    if (railRafRef.current != null) {
      cancelAnimationFrame(railRafRef.current);
      railRafRef.current = undefined;
    }
  }, []);

  /** JS progress bar: CSS keyframes often fail to restart / drift vs Slick fade + infinite. */
  const startRailAnim = useCallback(() => {
    stopRailAnim();
    railSlideStartRef.current = performance.now();
    railPauseSumRef.current = 0;
    railPauseBeginRef.current = null;
    setRailProgress(0);

    const tick = () => {
      const now = performance.now();
      const clock =
        railPauseBeginRef.current !== null
          ? railPauseBeginRef.current
          : now;
      const elapsed = clock - railSlideStartRef.current - railPauseSumRef.current;
      const t = Math.min(1, Math.max(0, elapsed / AUTOPLAY_MS));
      setRailProgress(t);
      if (t < 1) {
        railRafRef.current = requestAnimationFrame(tick);
      }
    };
    railRafRef.current = requestAnimationFrame(tick);
  }, [stopRailAnim]);

  useEffect(() => {
    let tmr: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      clearTimeout(tmr);
      tmr = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(refreshSliderLayout);
        });
      }, 80);
    };
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    return () => {
      clearTimeout(tmr);
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
    };
  }, [refreshSliderLayout]);

  useLayoutEffect(() => {
    refreshSliderLayout();
    if (!multi) return;
    const id = requestAnimationFrame(() => {
      sliderRef.current?.slickPlay?.();
    });
    return () => cancelAnimationFrame(id);
  }, [slides.length, i18n.language, refreshSliderLayout, multi]);

  useEffect(() => {
    if (!multi) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      sliderRef.current?.slickPlay?.();
      setRailEpoch((e) => e + 1);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [multi]);

  /* When slide *count* changes (not first mount), realign Slick + UI — avoids racing init on load. */
  useEffect(() => {
    const len = slides.length;
    if (len < 1) return;
    const prev = prevSlidesLengthRef.current;
    if (prev === undefined) {
      prevSlidesLengthRef.current = len;
      return;
    }
    if (prev === len) return;
    prevSlidesLengthRef.current = len;
    setActiveIndex(0);
    const t = window.setTimeout(() => {
      sliderRef.current?.slickGoTo(0, true);
      if (len > 1) sliderRef.current?.slickPlay?.();
      setRailEpoch((e) => e + 1);
    }, 0);
    return () => clearTimeout(t);
  }, [slides.length]);

  const readSlickIndex = useCallback((): number | null => {
    const slick = sliderRef.current as unknown as {
      innerSlider?: { state?: { currentSlide?: number } };
    } | null;
    const idx = slick?.innerSlider?.state?.currentSlide;
    return typeof idx === "number" ? idx : null;
  }, []);

  const handleAfterChange = useCallback((index: number) => {
    const len = slidesLenRef.current;
    if (len < 1) {
      setActiveIndex(0);
      setRailEpoch((e) => e + 1);
      return;
    }
    const i = ((index % len) + len) % len;
    setActiveIndex(i);
    setRailEpoch((e) => e + 1);
  }, []);

  /**
   * Align active segment + rail after mount, async slide data, or refresh.
   * Do not use react-slick `onReInit`: it runs on every InnerSlider update, including
   * each parent re-render while `railProgress` updates (would reset the bar every frame).
   */
  useLayoutEffect(() => {
    if (!multi || slides.length < 1) return;
    const t = window.setTimeout(() => {
      const idx = readSlickIndex();
      const len = slides.length;
      const i = idx !== null ? ((idx % len) + len) % len : 0;
      setActiveIndex(i);
      setRailEpoch((e) => e + 1);
    }, 0);
    return () => clearTimeout(t);
  }, [multi, slides.length, readSlickIndex]);

  useEffect(() => {
    if (!multi) return;
    startRailAnim();
    return stopRailAnim;
  }, [railEpoch, multi, startRailAnim, stopRailAnim]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !multi) return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!mq.matches) return;

    const onEnter = () => {
      railPauseBeginRef.current = performance.now();
    };
    const onLeave = () => {
      if (railPauseBeginRef.current !== null) {
        railPauseSumRef.current +=
          performance.now() - railPauseBeginRef.current;
        railPauseBeginRef.current = null;
      }
    };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [multi]);

  const openCta = (url: string) => {
    const u = url.trim();
    if (/^https?:\/\//i.test(u)) {
      window.open(u, "_blank", "noopener,noreferrer");
      return;
    }
    const path = u.startsWith("/") ? u : `/${u}`;
    navigate(path);
  };

  const openBooking = () => {
    if (cookies.apiKey) {
      navigate("/services");
      return;
    }

    navigate("/login", { state: { returnTo: "/services" } });
  };

  /* Slick + document `dir=rtl"` breaks fade/slide layout; keep slider LTR and handle RTL in UI only.
   * Memoize settings: a new object every render resets autoplay / animation timing.
   * pauseOnHover/pauseOnFocus off: sticky :hover on touch + focus on controls could pause forever;
   * desktop progress pause: fine-pointer hover on viewport (rAF rail below). */
  const sliderSettings = useMemo(
    () => ({
      dots: false,
      infinite: multi,
      arrows: false,
      fade: true,
      speed: FADE_MS,
      cssEase: "cubic-bezier(0.45, 0, 0.15, 1)",
      autoplay: multi,
      autoplaySpeed: AUTOPLAY_MS,
      pauseOnHover: false,
      pauseOnFocus: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      rtl: false,
      afterChange: handleAfterChange,
    }),
    [multi, handleAfterChange]
  );

  const goNext = useCallback(() => {
    sliderRef.current?.slickNext();
    sliderRef.current?.slickPlay();
  }, []);

  const goPrev = useCallback(() => {
    sliderRef.current?.slickPrev();
    sliderRef.current?.slickPlay();
  }, []);

  const goTo = useCallback((i: number) => {
    sliderRef.current?.slickGoTo(i);
    sliderRef.current?.slickPlay();
  }, []);

  const kicker = typeof current?.first_title === "string" ? current.first_title.trim() : undefined;
  const headline = typeof current?.second_title === "string" ? current.second_title.trim() : undefined;

  return (
    <section
      className={`ac-hero ac-hero--editorial box-section block-banner-home3 position-relative${isRtl ? " ac-hero--rtl" : ""}`}
      aria-roledescription="carousel"
      aria-label={t("home.hero.carouselLabel")}
    >
      <div className="ac-hero__shell w-100 position-relative">
        <div
          ref={viewportRef}
          className={`ac-hero__viewport ac-hero__viewport--fade${isLoading ? " ac-hero__viewport--loading" : ""}`}
        >
          <span className="ac-hero__sr-only" aria-live="polite">
            {multi
              ? t("home.hero.slideStatus", {
                  current: activeIndex + 1,
                  total: slides.length,
                })
              : ""}
          </span>

          <div className="ac-hero__slider-mount" dir="ltr">
            <Slider {...sliderSettings} ref={sliderRef}>
              {slides.map((item, index) => {
                const src = slideHeroImageSrc(item, index);
                return (
                  <div
                    key={`hero-${index}-${item.alternateBgOnly ? "alt" : "main"}`}
                  >
                    <div className="ac-hero__slide">
                      <div className="ac-hero__bg" aria-hidden>
                        <img
                          className="ac-hero__bg-img"
                          src={src}
                          alt=""
                          decoding="async"
                          draggable={false}
                          loading={index === 0 ? "eager" : "lazy"}
                          onError={(e) => {
                            const el = e.currentTarget;
                            if (el.dataset.acHeroFallback === "1") return;
                            el.dataset.acHeroFallback = "1";
                            el.src = fallbackBg(index % 2);
                          }}
                        />
                      </div>
                      {/* Dark readability scrim above photo, below caption (z-index) */}
                      <div
                        className="ac-hero__scrim ac-hero__scrim--readability"
                        aria-hidden
                      />
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>

          <div
            className="ac-hero__caption"
            dir={isRtl ? "rtl" : "ltr"}
          >
            <div className="container ac-hero__caption-inner ac-hero__caption-inner--editorial">
              <div className="ac-hero__caption-stack">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    className="ac-hero__caption-block ac-hero__caption-block--legible"
                    variants={captionContainer}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                  >
                    {kicker ? (
                      <motion.p
                        className="ac-hero__kicker ac-hero__kicker--editorial ac--font-expo"
                        variants={captionItem}
                      >
                        {kicker}
                      </motion.p>
                    ) : null}
                    {headline ? (
                      <motion.h2
                        className="ac-hero__headline ac-hero__headline--display ac--font-expo"
                        variants={captionItem}
                      >
                        {headline}
                      </motion.h2>
                    ) : null}
                    <motion.div
                      className="ac-hero__actions justify-content-start"
                      variants={captionItem}
                    >
                      <button
                        type="button"
                        className="btn ac-hero__cta ac-hero__cta--lux"
                        onClick={() => openCta("/offers")}
                      >
                        {t("home.hero.btn")}
                        {isRtl ? (
                          <FaArrowLeftLong
                            className="ac-hero__cta-icon"
                            aria-hidden
                          />
                        ) : (
                          <FaArrowRightLong
                            className="ac-hero__cta-icon"
                            aria-hidden
                          />
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn ac-hero__cta ac-hero__cta--lux ac-hero__cta--primary"
                        onClick={openBooking}
                      >
                        {t("home.hero.bookNow")}
                        {isRtl ? (
                          <FaArrowLeftLong
                            className="ac-hero__cta-icon"
                            aria-hidden
                          />
                        ) : (
                          <FaArrowRightLong
                            className="ac-hero__cta-icon"
                            aria-hidden
                          />
                        )}
                      </button>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {multi ? (
            <div
              className="ac-hero__controls ac-hero__controls--caption"
              dir="ltr"
            >
              <motion.button
                type="button"
                className="ac-hero__nav-btn ac-hero__nav-btn--prev"
                onClick={goPrev}
                aria-label={t("home.hero.prevSlide")}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 320,
                  damping: 26,
                  delay: 0.1,
                }}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
              >
                <HiChevronLeft
                  className="ac-hero__nav-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </motion.button>

              <div
                className="ac-hero__rail ac-hero__rail--editorial ac-hero__rail--caption"
                role="tablist"
                aria-label={t("home.hero.carouselLabel")}
              >
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={i === activeIndex}
                    aria-label={t("home.hero.goToSlide", {
                      n: i + 1,
                      total: slides.length,
                    })}
                    className={`ac-hero__rail-seg ${i === activeIndex ? "is-active" : ""}`}
                    onClick={() => goTo(i)}
                  >
                    <span className="ac-hero__rail-track" aria-hidden />
                    {i === activeIndex ? (
                      <span
                        className="ac-hero__rail-progress"
                        style={{
                          transform: `scaleX(${railProgress})`,
                          transformOrigin: isRtl
                            ? "right center"
                            : "left center",
                        }}
                        aria-hidden
                      />
                    ) : null}
                  </button>
                ))}
              </div>

              <motion.button
                type="button"
                className="ac-hero__nav-btn ac-hero__nav-btn--next"
                onClick={goNext}
                aria-label={t("home.hero.nextSlide")}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 320,
                  damping: 26,
                  delay: 0.18,
                }}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
              >
                <HiChevronRight
                  className="ac-hero__nav-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </motion.button>
            </div>
          ) : null}

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
